/**
 * IBAS admissions content — the single source of truth for the /admissions page
 * (Why study with IBAS · Find your admission advisor · Admission process · Build
 * your custom study track · FAQ).
 *
 * SOURCING (ibas-brand skill, hard rule): every institutional fact here is drawn
 * only from the branding folder, https://ibas.edu.eu, the source-derived content
 * bank cache, or the user directly — nothing is invented. Anything not verified
 * from those sources is LEFT OUT and routed to "speak with an adviser" rather than
 * filled with a plausible-sounding placeholder. Programme/specialisation data for
 * the custom-track builder is reused from data/programmes.ts (not duplicated).
 *
 * Claims Rule: figures carry their year/scope/source; dated terms (application
 * fee, minimum-cohort rule) carry the "effective 1 October 2026" qualifier because
 * they belong to the post-01.10.2026 Terms & Conditions set.
 */

import {
  Award,
  CalendarClock,
  Globe2,
  GraduationCap,
  LifeBuoy,
  Mail,
  MapPin,
  Phone,
  type LucideIcon,
} from 'lucide-react';

/* ────────────────────────────────────────────────────────────────────────────
 * WHY STUDY WITH IBAS
 * ────────────────────────────────────────────────────────────────────────── */

export interface WhyPillar {
  title: string;
  description: string;
  Icon: LucideIcon;
}

/**
 * Four reasons to study with IBAS, each grounded in verified accreditation,
 * flexibility, reach and support facts. No guarantees of employment or
 * recognition beyond what the sources state.
 */
export const WHY_PILLARS: readonly WhyPillar[] = [
  {
    title: 'Recognition and credibility',
    description:
      'IBAS holds ACBSP accreditation for its MBA and DBA programmes — the Accreditation Council for Business Schools and Programs is recognised by CHEA in the United States. IBAS is also QS 5-Star rated (2026).',
    Icon: Award,
  },
  {
    title: 'Built around your life',
    description:
      'Online programmes follow the Flexi-Learn model: rolling monthly intakes, the study mode that suits you, and the freedom to pause and resume at any time.',
    Icon: CalendarClock,
  },
  {
    title: 'Global by design',
    description:
      'A community of 63+ student nationalities across eight global learning centres on six continents, with more than 3,500 graduates worldwide.',
    Icon: Globe2,
  },
  {
    title: 'Support across the journey',
    description:
      'Our advisers help you choose your route before enrolment, and the registrar and support teams stay with you throughout your studies — the CARE half of how IBAS is built.',
    Icon: LifeBuoy,
  },
];

/** Institution-wide proof figures. Caption each with its scope where shown. */
export interface AdmissionsStat {
  value: number;
  suffix: string;
  label: string;
}

export const ADMISSIONS_STATS: readonly AdmissionsStat[] = [
  { value: 3500, suffix: '+', label: 'Graduates worldwide' },
  { value: 63, suffix: '+', label: 'Student nationalities' },
  { value: 8, suffix: '', label: 'Global learning centres' },
  { value: 25, suffix: '+', label: 'Faculty members' },
];

/* ────────────────────────────────────────────────────────────────────────────
 * FIND YOUR ADMISSION ADVISOR
 * ────────────────────────────────────────────────────────────────────────── */

export interface AdvisorChannel {
  label: string;
  /** The verified contact value shown to the visitor. */
  value: string;
  /** href for the action (mailto:, tel:, or a maps link). */
  href?: string;
  /** When / for what this channel is available (scope from the mandated table). */
  note: string;
  Icon: LucideIcon;
}

/**
 * Verified admissions contact channels (content bank / mandated Contact table).
 * IBAS has not published individual named advisers to us, so we present the real
 * channels rather than invented advisor profiles.
 */
export const ADVISOR_CHANNELS: readonly AdvisorChannel[] = [
  {
    label: 'Email admissions',
    value: 'admissions@ibas.edu.eu',
    href: 'mailto:admissions@ibas.edu.eu',
    note: 'Applications, documents and the admission process — before enrolment.',
    Icon: Mail,
  },
  {
    label: 'Call an adviser',
    value: '+41 76 623 7895',
    href: 'tel:+41766237895',
    note: 'Available Monday & Thursday, 09:30–17:00 (CET).',
    Icon: Phone,
  },
  {
    label: 'Visit the campus',
    value: 'Gewerbestrasse 14, 8800 Thalwil, Switzerland',
    note: 'Home of IBAS on-site study, near Zürich.',
    Icon: MapPin,
  },
];

/* ────────────────────────────────────────────────────────────────────────────
 * ADMISSION PROCESS  (Online / On-site)
 * ────────────────────────────────────────────────────────────────────────── */

export type AdmissionTrackKey = 'online' | 'onsite';

export interface ProcessStep {
  title: string;
  description: string;
}

export interface AdmissionTrack {
  key: AdmissionTrackKey;
  label: string;
  /** One-line framing of who this route is for. */
  tagline: string;
  steps: readonly ProcessStep[];
  Icon: LucideIcon;
}

/**
 * The two admission routes. Steps are kept at the level the sources support —
 * enquiry, application + documents, application fee, and start — with document
 * specifics routed to the adviser rather than invented. Fee amounts and refund
 * terms are published only in the Terms & Conditions; the fee step points there
 * via the adviser rather than quoting figures.
 */
export const ADMISSION_TRACKS: readonly AdmissionTrack[] = [
  {
    key: 'online',
    label: 'Online (Flexi-Learn)',
    tagline: 'Study fully online, on rolling monthly intakes.',
    Icon: GraduationCap,
    steps: [
      {
        title: 'Speak with an adviser',
        description:
          'Talk through your goals and choose the programme and study mode that fit your life. Our advisers guide you before enrolment.',
      },
      {
        title: 'Submit your application',
        description:
          'Complete your application and share the supporting documents your adviser confirms for your chosen programme.',
      },
      {
        title: 'Pay the application fee',
        description:
          'Your adviser will confirm the application fee and payment details, as set out in the Terms & Conditions.',
      },
      {
        title: 'Begin on a monthly intake',
        description:
          'Modules open on a rolling monthly basis, so you can start when it suits you — and pause whenever you need to.',
      },
    ],
  },
  {
    key: 'onsite',
    label: 'On-site (Thalwil)',
    tagline: 'Study on campus in Switzerland, on scheduled intakes.',
    Icon: MapPin,
    steps: [
      {
        title: 'Speak with an adviser',
        description:
          'Discuss the on-site routes — the Diploma, BBA and Master’s programmes — and confirm intake dates and entry requirements.',
      },
      {
        title: 'Submit your application',
        description:
          'Complete your application and share the supporting documents your adviser confirms for your chosen programme.',
      },
      {
        title: 'Pay the application fee',
        description:
          'Your adviser will confirm the application fee and payment details, as set out in the Terms & Conditions.',
      },
      {
        title: 'Join a scheduled intake',
        description:
          'On-site programmes run on scheduled intakes at the IBAS campus in Thalwil, near Zürich.',
      },
    ],
  },
];

/* ────────────────────────────────────────────────────────────────────────────
 * FREQUENTLY ASKED QUESTIONS
 * ────────────────────────────────────────────────────────────────────────── */

export interface FaqItem {
  question: string;
  answer: string;
}

/** Every answer is grounded in the verified content bank / T&C sources. */
export const ADMISSIONS_FAQS: readonly FaqItem[] = [
  {
    question: 'How do intakes work?',
    answer:
      'Online programmes follow the Flexi-Learn model, with modules opening on rolling monthly intakes, so there is no fixed start date to wait for. On-site programmes at the Thalwil campus run on scheduled intakes.',
  },
  {
    question: 'Can I pause my studies?',
    answer:
      'Yes. With Flexi-Learn you can pace the programme around work and life and pause your studies at any time, then pick up exactly where you left off.',
  },
  {
    question: 'What study modes are available?',
    answer:
      'You can study Self-Study (asynchronous), Live Online (synchronous) or Blended — a combination of the two. On-site programmes are studied on campus in Thalwil.',
  },
  {
    question: 'How long does the Flexi-MBA take?',
    answer:
      'The Flexi-MBA is completed in a minimum of eight months and carries 60–90 ECTS (a minimum of 60), paced entirely around your schedule.',
  },
  {
    question: 'Are IBAS programmes accredited?',
    answer:
      'IBAS holds ACBSP accreditation for its MBA and DBA programmes; ACBSP is recognised by the Council for Higher Education Accreditation (CHEA) in the United States. IBAS is also EduQua Certified and listed on the Swiss Private School Register.',
  },
  {
    question: 'Is there an application fee?',
    answer:
      'Yes — an application fee applies. Your adviser will confirm the amount and payment details, as set out in the Terms & Conditions.',
  },
  {
    question: 'Can I choose a specialisation?',
    answer:
      'The Flexi-MBA offers 16 specialisations across four tracks. Under the terms effective 1 October 2026, a specialisation runs subject to a minimum of 10 students.',
  },
  {
    question: 'How do I get help choosing?',
    answer:
      'Email admissions@ibas.edu.eu or ask to speak with an adviser. Our admissions team supports you with applications, documents and the admission process before enrolment.',
  },
];
